import { useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import api from '../../services/api';

/**
 * A contractor's call-out fee, from the client's side.
 *
 * The point of the panel is that the client is never guessing: what the money
 * is for, where it sits, what gets it back, and — the line that matters most
 * for someone who has been scammed before — that nobody should ever ask them
 * for cash. Mirrors the states in VisitFeeService.
 *
 * `source` is 'assessment_request' or 'quote_request'; `request` is the row.
 */
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const NO_CASH = 'Nobody should ask you for cash. Any call-out fee is paid here and held by Eqaya.';

export default function CallOutFeePanel({ source, request, providerName = 'The contractor', onChanged }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const amount = request?.visit_fee_amount;
  const status = request?.visit_fee_status;
  if (!amount || !status) return null;

  const id = request.id;
  const call = async (action, key) => {
    setBusy(key);
    setError('');
    try {
      const res = await api.post(`/visit-fees/${source}/${id}/${action}`);
      if (action === 'checkout' && res.data?.data?.checkoutUrl) {
        window.location.href = res.data.data.checkoutUrl;
        return;
      }
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const spinner = (key) => (busy === key ? <CircularProgress size={16} color="inherit" /> : null);
  const awaitingConfirmation = status === 'held' && request.visit_fee_release_due_at;

  if (status === 'proposed') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>{providerName} is asking a {money(amount)} call-out fee for this visit</AlertTitle>
        <Typography variant="body2">
          {request.visit_fee_description ? `It covers ${request.visit_fee_description}. ` : ''}
          This is travel money, not a payment for the work. You pay it here, Eqaya holds it, and it
          only reaches {providerName} after the visit. If the visit does not happen you get it back.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>{NO_CASH}</Typography>
        {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
          <Button size="small" variant="contained" disabled={!!busy}
            startIcon={spinner('pay')} onClick={() => call('checkout', 'pay')}>
            Pay {money(amount)}
          </Button>
          <Button size="small" color="inherit" disabled={!!busy}
            startIcon={spinner('decline')} onClick={() => call('decline', 'decline')}>
            Decline the fee
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (status === 'held' || status === 'refunding') {
    return (
      <Alert severity={awaitingConfirmation ? 'warning' : 'success'} sx={{ mb: 2 }}>
        <AlertTitle>
          {awaitingConfirmation
            ? `Did the visit happen? — ${money(amount)} call-out fee`
            : `${money(amount)} call-out fee paid and held`}
        </AlertTitle>
        {awaitingConfirmation ? (
          <>
            <Typography variant="body2">
              {providerName} says they visited. Confirm and we release their fee, or tell us it did
              not happen and we refund you. If you do nothing we release it 72 hours after they
              marked it done.
            </Typography>
            {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
              <Button size="small" variant="contained" disabled={!!busy}
                startIcon={spinner('confirm')} onClick={() => call('confirm-visit', 'confirm')}>
                Confirm — release the fee
              </Button>
              <Button size="small" color="error" variant="outlined" disabled={!!busy}
                startIcon={spinner('dispute')} onClick={() => call('dispute', 'dispute')}>
                This visit didn&apos;t happen
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="body2">
              Eqaya is holding it until the visit happens, then it goes to {providerName}. Cancel
              any time before then and it comes straight back to your card.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>{NO_CASH}</Typography>
            {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" color="inherit" disabled={!!busy}
                startIcon={spinner('cancel')} onClick={() => call('cancel', 'cancel')}>
                Cancel and get a refund
              </Button>
            </Box>
          </>
        )}
      </Alert>
    );
  }

  if (status === 'released') {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{money(amount)} call-out fee released</strong> to {providerName} — the visit was confirmed.
        </Typography>
      </Alert>
    );
  }

  if (status === 'refunded') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{money(amount)} call-out fee refunded</strong> to your card
          {request.visit_fee_refund_reason === 'visit_not_done' ? ' — the visit did not happen.'
            : request.visit_fee_refund_reason === 'expired' ? ' — no visit took place within 14 days.'
              : '.'}
        </Typography>
      </Alert>
    );
  }

  // cancelled
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2">
        You declined the {money(amount)} call-out fee. Nothing was charged, and the visit request
        itself still stands — talk to {providerName} about it.
      </Typography>
    </Alert>
  );
}
